import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useImportFreeBuilderDraft, type ImportFreeBuilderBody } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";

/**
 * The free quote / invoice generator on quotrak.com hands its draft to the
 * signup page in the URL fragment (`#draft=<base64url JSON>`). The signup page
 * stashes it under this key (see pages/auth.tsx) so it survives the
 * email-verification round trip; this component, mounted inside the
 * authenticated shell, imports it once and takes the user to the result.
 */
export const PENDING_FREE_BUILDER_DRAFT_KEY = "quotrak:pendingFreeBuilderDraft";

type Payload = { v?: number; mode?: string; draft?: unknown };

/** Decode the `#draft=` fragment the public generator produces. Returns null if absent or malformed. */
export function readDraftFromHash(hash: string): Payload | null {
  const m = /[#&]draft=([A-Za-z0-9_-]+)/.exec(hash || "");
  if (!m) return null;
  try {
    const b64 = m[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(escape(atob(padded)));
    const payload = JSON.parse(json) as Payload;
    if (!payload || typeof payload !== "object" || !payload.draft) return null;
    return payload;
  } catch {
    return null;
  }
}

export function stashPendingDraft(payload: Payload) {
  try {
    localStorage.setItem(PENDING_FREE_BUILDER_DRAFT_KEY, JSON.stringify(payload));
  } catch {
    /* private mode etc. — the signup still works, the draft just stays in the visitor's browser */
  }
}

export function hasPendingDraft(): boolean {
  try {
    return !!localStorage.getItem(PENDING_FREE_BUILDER_DRAFT_KEY);
  } catch {
    return false;
  }
}

export function PendingFreeBuilderImport() {
  const importDraft = useImportFreeBuilderDraft();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(PENDING_FREE_BUILDER_DRAFT_KEY);
    } catch {
      return;
    }
    if (!raw) return;
    started.current = true;
    // Remove it first so a failure can't loop on every page load.
    try { localStorage.removeItem(PENDING_FREE_BUILDER_DRAFT_KEY); } catch { /* ignore */ }

    let payload: Payload;
    try {
      payload = JSON.parse(raw) as Payload;
    } catch {
      return;
    }
    const mode: ImportFreeBuilderBody["mode"] = payload.mode === "invoice" ? "invoice" : "quote";
    const draft = payload.draft as ImportFreeBuilderBody["draft"];
    if (!draft || !Array.isArray(draft.lineItems)) return;

    importDraft.mutate(
      { data: { mode, draft } },
      {
        onSuccess: (result) => {
          queryClient.invalidateQueries();
          const noun = result.kind === "invoice" ? "invoice" : "quote";
          toast({
            title: `Your ${noun} is here`,
            description: `We brought over the ${noun} you built on quotrak.com, costs and all.`,
          });
          setLocation(result.kind === "invoice" ? `/invoices/${result.id}` : `/quotes/${result.id}`);
        },
        onError: (err) => {
          toast({
            title: "Couldn't import your draft",
            description: err instanceof Error ? err.message : "It's still saved in your browser on quotrak.com.",
            variant: "destructive",
          });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
